import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stageOptions = [
  "I am just getting started",
  "I need to look more professional",
  "I have a business but my marketing is inconsistent",
  "I need more leads and better follow-up",
  "I manage a team or agency",
  "I am not sure yet",
];

const serviceOptions = [
  "Branding",
  "Website",
  "Google Business Profile",
  "Social Media",
  "Listing Templates",
  "Business Cards / Print",
  "AI Receptionist",
  "CRM / Automation",
  "Paid Ads",
  "SEO / AI Visibility",
  "Agency Systems",
  "Not sure yet",
];

const budgetOptions = [
  "Under $500 / month",
  "$500 - $1,000 / month",
  "$1,000 - $2,500 / month",
  "$2,500 - $5,000 / month",
  "$5,000+ / month",
  "Not sure yet",
];

const timelineOptions = [
  "As soon as possible",
  "Within 1 month",
  "Within 3 months",
  "Just exploring",
];

export default function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    profession: "",
    company: "",
    location: "",
    website: "",
    current_stage: "",
    help_needed: "",
    services_of_interest: [],
    budget_range: "",
    timeline: "",
    preferred_contact: "Email",
    notes: "",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (svc) => {
    setForm((f) => ({
      ...f,
      services_of_interest: f.services_of_interest.includes(svc)
        ? f.services_of_interest.filter((s) => s !== svc)
        : [...f.services_of_interest, svc],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError("Please provide your name and email.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-form-success"
        className="relative border border-[#C8A24B]/40 bg-[#0e0e0e] p-10 md:p-14 rounded-sm"
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B]" />
        <div className="w-14 h-14 border border-[#C8A24B] flex items-center justify-center mb-8">
          <Check size={22} className="text-[#C8A24B]" />
        </div>
        <h3 className="font-heading text-3xl md:text-4xl text-white mb-4">
          Your request has been received.
        </h3>
        <p className="text-[#a1a1aa] leading-relaxed max-w-2xl">
          Agent Marketing will review your current stage, goals, services of
          interest, and timeline. The next step is a short fit conversation so
          we can understand where you are now and what should happen first.
        </p>
        <p className="text-[#71717a] leading-relaxed max-w-2xl mt-4">
          You do not need to know exactly what package you need yet. That is
          part of what we help determine.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <Field label="Full name" required testId="input-name">
        <input
          data-testid="input-name-field"
          className={inputCls}
          value={form.name}
          onChange={(e) => upd("name", e.target.value)}
          placeholder="Sarah Chen"
          required
        />
      </Field>
      <Field label="Email address" required testId="input-email">
        <input
          data-testid="input-email-field"
          type="email"
          className={inputCls}
          value={form.email}
          onChange={(e) => upd("email", e.target.value)}
          placeholder="sarah@yourbrokerage.com"
          required
        />
      </Field>
      <Field label="Phone" testId="input-phone">
        <input
          data-testid="input-phone-field"
          className={inputCls}
          value={form.phone}
          onChange={(e) => upd("phone", e.target.value)}
          placeholder="Optional"
        />
      </Field>
      <Field label="Profession" testId="input-profession">
        <input
          data-testid="input-profession-field"
          className={inputCls}
          value={form.profession}
          onChange={(e) => upd("profession", e.target.value)}
          placeholder="Real estate agent, mortgage, insurance..."
        />
      </Field>
      <Field label="Company or brokerage" testId="input-company">
        <input
          data-testid="input-company-field"
          className={inputCls}
          value={form.company}
          onChange={(e) => upd("company", e.target.value)}
          placeholder="Optional"
        />
      </Field>
      <Field label="Location" testId="input-location">
        <input
          data-testid="input-location-field"
          className={inputCls}
          value={form.location}
          onChange={(e) => upd("location", e.target.value)}
          placeholder="City, Province / State"
        />
      </Field>
      <Field label="Website" testId="input-website">
        <input
          data-testid="input-website-field"
          className={inputCls}
          value={form.website}
          onChange={(e) => upd("website", e.target.value)}
          placeholder="https://..."
        />
      </Field>
      <Field label="Preferred contact" testId="input-preferred">
        <select
          data-testid="input-preferred-field"
          className={inputCls}
          value={form.preferred_contact}
          onChange={(e) => upd("preferred_contact", e.target.value)}
        >
          <option>Email</option>
          <option>Phone</option>
          <option>Text</option>
        </select>
      </Field>

      <Field
        label="Current stage"
        testId="input-current-stage"
        className="md:col-span-2"
      >
        <select
          data-testid="input-stage-field"
          className={inputCls}
          value={form.current_stage}
          onChange={(e) => upd("current_stage", e.target.value)}
        >
          <option value="">Select where you are today</option>
          {stageOptions.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </Field>

      <Field
        label="What do you need help with?"
        testId="input-help"
        className="md:col-span-2"
      >
        <textarea
          data-testid="input-help-field"
          rows={4}
          className={`${inputCls} resize-none`}
          value={form.help_needed}
          onChange={(e) => upd("help_needed", e.target.value)}
          placeholder="Tell us what you would like to improve first."
        />
      </Field>

      <Field
        label="Services of interest"
        testId="input-services"
        className="md:col-span-2"
      >
        <div className="flex flex-wrap gap-2">
          {serviceOptions.map((svc) => {
            const active = form.services_of_interest.includes(svc);
            return (
              <button
                key={svc}
                type="button"
                data-testid={`service-chip-${svc}`}
                onClick={() => toggleService(svc)}
                className={`px-3.5 py-2 text-xs md:text-[13px] border rounded-sm transition-all ${
                  active
                    ? "border-[#C8A24B] bg-[#C8A24B]/[0.08] text-[#C8A24B]"
                    : "border-[#27272a] text-[#a1a1aa] hover:border-[#3f3f46] hover:text-white"
                }`}
              >
                {svc}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Monthly budget range" testId="input-budget">
        <select
          data-testid="input-budget-field"
          className={inputCls}
          value={form.budget_range}
          onChange={(e) => upd("budget_range", e.target.value)}
        >
          <option value="">Select range</option>
          {budgetOptions.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </Field>
      <Field label="Timeline" testId="input-timeline">
        <select
          data-testid="input-timeline-field"
          className={inputCls}
          value={form.timeline}
          onChange={(e) => upd("timeline", e.target.value)}
        >
          <option value="">Select timeline</option>
          {timelineOptions.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </Field>

      <Field
        label="Notes"
        testId="input-notes"
        className="md:col-span-2"
      >
        <textarea
          data-testid="input-notes-field"
          rows={3}
          className={`${inputCls} resize-none`}
          value={form.notes}
          onChange={(e) => upd("notes", e.target.value)}
          placeholder="Anything else we should know."
        />
      </Field>

      {error && (
        <div
          data-testid="contact-form-error"
          className="md:col-span-2 text-sm text-[#dc2626] border border-[#7f1d1d] bg-[#7f1d1d]/10 p-3 rounded-sm"
        >
          {error}
        </div>
      )}

      <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[#27272a]">
        <p className="text-xs text-[#71717a] max-w-md leading-relaxed">
          By submitting, you agree to be contacted about your request. We do
          not sell your information. See our Privacy Policy.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-form-submit-button"
          className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Submitting
            </>
          ) : (
            <>
              Build My Growth Plan <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#27272a] rounded-sm px-4 py-3 text-white placeholder:text-[#52525b] focus:border-[#C8A24B] focus:ring-1 focus:ring-[#C8A24B] outline-none transition-colors text-[15px]";

function Field({ label, required, children, testId, className = "" }) {
  return (
    <label
      data-testid={testId}
      className={`block ${className}`}
    >
      <span className="block text-[10px] tracking-[0.24em] uppercase text-[#71717a] mb-2 font-medium">
        {label} {required && <span className="text-[#C8A24B]">*</span>}
      </span>
      {children}
    </label>
  );
}
